import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: 'ASMR导航',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr-navigation.com/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://asmr-navigation.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://asmr-navigation.com/article/${article.slug}` },
    ],
  };

  return (
    <Layout>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={`asmr网站,asmr网站推荐,${article.tags.join(',')},asmr助眠网站,asmr在线听,asmr资源平台`}
        canonicalUrl={`/article/${slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <article className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Article Header */}
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {category && (
              <Link
                to={`/category/${article.category}`}
                className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer"
              >
                {category.name}
              </Link>
            )}
            {article.tags.map((tag) => (
              <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-xs text-foreground-400">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={article.title}
          />
        </div>

        {/* Article Content */}
        <div
          className="prose prose-sm md:prose-base max-w-none
            prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
            prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
            prose-strong:text-foreground-900
            prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
            prose-li:text-foreground-700
            [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-4
            [&_p]:text-sm md:[&_p]:text-base
          "
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Article Footer */}
        <div className="mt-10 pt-6 border-t border-background-200/70">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span>分享文章：</span>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </a>
            </div>
            <Link
              to={category ? `/category/${article.category}` : '/'}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
            >
              ← 返回{category?.name || '列表'}
            </Link>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-5">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group block p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-32 rounded-md overflow-hidden mb-3">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={ra.title}
                    />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {ra.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1">{ra.publishDate}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
}